import React from 'react'
import Head from 'next/head'
import Cookies from 'universal-cookie'
import axios from 'axios'
import { get } from 'lodash'
import { useRouter } from 'next/dist/client/router'
import { useTranslation } from 'react-i18next'

import '@lib/i18n/config'
import style from '@styles/Page.module.scss'
import { AccountSettings } from '@components/account/AccountSettings'
import { BasicHeader } from '@components/BasicHeader'
import { BasicTopBar } from '@components/BasicTopBar'
import { LeftNavigation } from '@components/LeftNavigation'
import { Footer } from '@components/Footer'
import { FooterType, TrainingType } from '@contentful/types'
import { UpdateAccountForm } from '@components/account/UpdateAccountForm'
import { getFooter, getTraining } from '@contentful/contentfulService'
import { getLocalizedPath } from '@lib/i18n'
import { useChangeLanguageFromPath } from '@hooks/useChangeLanguageFromPath'
import { useValidateUserToken } from '@hooks/useValidateUserToken'
import { UserType } from '@queries/types/user'
import { setProgressQuery } from '@queries/setProgressQuery'
import { emptyUser } from '@lib/constants'

interface Props {
  footer: FooterType
  training: TrainingType
}

export default function AccountPage({ footer, training }: Props) {
  const { t, i18n } = useTranslation()
  useChangeLanguageFromPath(i18n)
  const router = useRouter()

  const { user } = useValidateUserToken(i18n.language)

  let validUser: UserType = user === null ? emptyUser : user

  const onLanguageChange = async (locale: string) => {
    if (user) {
      setProgressQuery({ locale, token: user.token })
    }
    router.replace(getLocalizedPath(locale, '/account'))
  }

  const footerCopyright = get(footer, 'fields.copyright')
  const footerLinks = get(footer, 'fields.links')

  const pageTitle = t('account.pageTitle')

  const { email } = validUser

  const onSignOutClick = async () => {
    const cookies = new Cookies()
    const jti = cookies.get('jti')

    await axios('/api/users/sign-out', { method: 'post', data: { jti } })

    router.push(getLocalizedPath(i18n.language, '/sign-in'))
  }

  return (
    <div>
      <Head>
        <title>{pageTitle}</title>
        <link rel="icon" href="/favicon.ico" />
      </Head>

      <main className={style.main}>
        <LeftNavigation
          completedPageIds={user?.progress?.completed_pages}
          training={training}
          currentSection={null}
          currentPageId="accountPageId"
          currentPath="/account"
          locale={i18n.language}
          onLanguageChange={onLanguageChange}
        />

        <BasicTopBar title={t('account.topBarTitle')} />

        <div className={style.contentContainer}>
          <div className={style.content}>
            <div className={style.innerContent}>
              <BasicHeader title={t('account.title')} subtitle={pageTitle} />
              <AccountSettings
                title={t('account.signOut.title')}
                description={t('account.signOut.description', { email })}
                buttonText={t('account.signOut.title')}
                onButtonClick={onSignOutClick}
              />
              <UpdateAccountForm user={validUser} />
            </div>
          </div>
        </div>

        <Footer copyright={footerCopyright} links={footerLinks} />
      </main>
    </div>
  )
}

export async function getStaticProps() {
  const locale = 'en-US'
  const footer = await getFooter(locale)
  const training = await getTraining(locale)

  return { props: { footer, training } }
}
